import { Button, CircularProgress, MenuItem, TextField, Select, Checkbox, FormGroup, FormControlLabel, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { API } from 'aws-amplify';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import EmailChips from '../../../common/EmailChips';
import { convertToTimeZone, TIMEZONES } from '../../../utils/utils';
import InputCell from "../../../common/form/InputCell";
import _ from "lodash";
import { isExpandedSentimentTypesEnabled } from '../../../shared/access';

function ActivityDigestEmail(props) {
    const isExpandedSentiment = isExpandedSentimentTypesEnabled();

    const FREQUENCIES = ['DAILY', 'WEEKLY', 'BIWEEKLY', 'MONTHLY'];
    const FREQUENCY_LABELS = {
        'DAILY': 'Daily',
        'WEEKLY': 'Weekly',
        'BIWEEKLY': 'Biweekly',
        'MONTHLY': 'Monthly',
    };
    const FEEDBACKS = {
        'POSITIVE': 'Positive',
        'MIXED': 'Mixed',
        'NEGATIVE': 'Negative',
        'NEUTRAL': 'Neutral',
        ...(isExpandedSentiment && {
            'NONE': 'None',
            'OTHER': 'Other',
        }),
    };
    const TIMEZONE = props?.user?.aboutMe?.currentTenant?.timezoneName;

    const [selectedDayOfWeek, setSelectedDayOfWeek] = useState(0);
    const [selectedDayOfMonth, setSelectedDayOfMonth] = useState(1);
    const [hour, setHour] = useState(0);
    const [minutes, setMinutes] = useState(0);
    const [subscribers, setSubscribers] = useState([]);
    const [timeZone, setTimeZone] = useState(0);
    const [currentSetting, setCurrentSetting] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [disableButton, setDisableButton] = useState(false);
    const [digestName, setDigestName] = useState('');
    const [selectedFeedbacks, setSelectedFeedbacks] = useState(Object.fromEntries(Object.keys(FEEDBACKS).map(k => [k, false])));
    const [noteworthy, setNoteworthy] = useState(false);
    const [currentType, setCurrentType] = useState(props.type);

    const [currentFrequency, setCurrentFrequency] = useState(FREQUENCIES[0]);
    const saveButtonRef = useRef(null);
    const cancelButtonRef = useRef(null);

    const DAYS_OF_WEEK = [
        { label: 'Sun', value: 0 },
        { label: 'Mon', value: 1 },
        { label: 'Tue', value: 2 },
        { label: 'Wed', value: 3 },
        { label: 'Thu', value: 4 },
        { label: 'Fri', value: 5 },
        { label: 'Sat', value: 6 },
    ];

    const DAYS_OF_MONTH = [...Array(31).keys()].map(n => ({ label: n + 1, value: n + 1 }));

    const MINUTES_OPTIONS = ['00', '30'];

    const selectDayOfWeek = i => setSelectedDayOfWeek(i);

    const updateHour = event => {
        const VALUE = Math.floor(Number(event.target.value));
        if (VALUE >= 0 && VALUE <= 23 && Number.isInteger(VALUE)) setHour(VALUE)
    }

    const updateMinutes = event => {
        const VALUE = Math.floor(Number(event.target.value));
        if (VALUE >= 0 && VALUE <= 59 && Number.isInteger(VALUE)) setMinutes(VALUE.toString().padStart(2, '0'))
    }

    const validateInput = event => {

        const ASCIICode = (event.which) ? event.which : event.keyCode;
        const ASCII_NINE_CODE = 57, ASCII_ZERO_CODE = 48;

        if (!(ASCIICode >= ASCII_ZERO_CODE && ASCIICode <= ASCII_NINE_CODE)) {
            event.preventDefault();
        }
    }

    const addEmails = (...emails) => {
        const VALUE = [...subscribers.concat(emails)];

        setSubscribers(VALUE);
    }

    const removeEmail = index => {
        const VALUE = [...subscribers];

        VALUE.splice(index, 1);

        setSubscribers(VALUE);
    }

    const getFrequencyValue = () => {
        return {
            'DAILY': hour,
            'WEEKLY': selectedDayOfWeek,
            'BIWEEKLY': selectedDayOfWeek,
            'MONTHLY': selectedDayOfMonth
        }[currentFrequency]
    }
    const onCancel = () => {
        props.history.goBack();
    }

    const createDigestData = () => ({
        ...currentSetting,
        active: true,
        name: digestName,
        receivers: subscribers.join(';'),
        frequency: currentFrequency,
        frequencyValue: getFrequencyValue(),
        hoursValue: hour,
        minutesValue: Number(minutes),
        dayOfWeek: selectedDayOfWeek,
        type: currentType,
        feedback: Object.keys(selectedFeedbacks).filter(f => selectedFeedbacks[f]).join(','),
        timezone: props?.user?.aboutMe?.currentTenant?.timezoneName,
        noteworthy: isExpandedSentiment ? noteworthy : false,
    })


    const isAnyFeedbackSelected = () => {
        return Object.values(selectedFeedbacks).some(feedback => feedback);
    };

    const saveSubscription = () => {
        let error = null;
        if (!isAnyFeedbackSelected()) error= "Feedback Types not selected";
        if (!subscribers || subscribers.length === 0) error = "Receivers list cannot be empty";
        if(!digestName && digestName === '') error = "Digest Name cannot be empty";
        if (error) {
            toast.error(error, { position: "top-center" });
            return;
        }

        const DATA = createDigestData();
        setSaving(true);

        let savingRequest;

        if (currentSetting?.id) {
            savingRequest = API.put('Core', `/api/v1/digest/settings/${currentSetting.id}`, { body: DATA })
                .then(r => {
                    const SETTING = r;

                    if (SETTING) {
                        setCurrentSetting(SETTING);
                        toast.success("Digest Setting Updated!", { position: "top-center" });
                    }
                })
        }
        else {

            savingRequest = API.post('Core', '/api/v1/digest/settings', { body: DATA })
                .then(r => {
                    const SETTING = r;

                    if (SETTING) {
                        setCurrentSetting(SETTING);
                        toast.success("Digest Setting Created!", { position: "top-center" });
                    }
                })
        }

        savingRequest.finally(() => {
            setSaving(false);
            cancelButtonRef.current?.focus();
        });
    }

    let updateFeedback = feedback => {
        const SELECTED_FEEDBACKS = { ...selectedFeedbacks };
        SELECTED_FEEDBACKS[feedback] = !SELECTED_FEEDBACKS[feedback];
        setSelectedFeedbacks({ ...SELECTED_FEEDBACKS });
    }

    useEffect(() => {
        const ID = props?.match?.params?.id;
        if (!ID) return;

        setLoading(true);
        API.get('Core', `/api/v1/digest/settings/${ID}`)
            .then(r => {
                setCurrentSetting(r || {});
                if (r) {
                    const SELECTED_FEEDBACK = selectedFeedbacks;

                    setCurrentFrequency(r.frequency);
                    setDigestName(r.name);
                    setHour(r.hoursValue);
                    setCurrentType(r.type);
                    setMinutes((MINUTES_OPTIONS.map(Number).includes(r.minutesValue) ? r.minutesValue : 0).toString().padStart(2, '0'));
                    setSelectedDayOfWeek(r.dayOfWeek);

                    if (r.frequency === 'MONTHLY') setSelectedDayOfMonth(r.frequencyValue);
                    if (r.feedback != null) r.feedback.split(',').forEach(f => SELECTED_FEEDBACK[f] = true);
                    if (isExpandedSentiment) setNoteworthy(!!r.noteworthy);

                    if (r.type) setCurrentType(r.type);

                    setSelectedFeedbacks({ ...SELECTED_FEEDBACK });
                    setSubscribers(r.receivers ? r.receivers.split(';') : []);
                    setTimeZone(r.timezone ? TIMEZONES.map(t => t.value).indexOf(r.timezone) : 0);
                }
            })
            .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    let getTimeValue = (value) => {
        if (value == null) return '';
        else if (value < 10) return `0${value}`;
        else return value.toString();
    }

    return (
        <div className='emailDigestSubscriptionsView'>
            {!loading ? <>
                <section>
                    <div id="digestNameLabel" className="emailDigestSubscriptionsHeader" style={{ display: 'block' }}>
                        Digest Name <span aria-hidden="true" style={{ color: 'red' }}>*</span>
                    </div>
                    <TextField
                        placeholder='Digest Name input field'
                        value={digestName}
                        onChange={event => setDigestName(event.target.value)}
                        autoComplete="off"
                        inputProps={{ 'aria-labelledby': 'digestNameLabel', 'aria-required': 'true', 'data-1p-ignore': true, 'data-lpignore': true }}
                        style={{ display: 'block' }}
                    />
                </section>

                <section>
                    <div className="emailDigestSubscriptionsHeader" id="frequencyLabel">
                        Frequency <span aria-hidden="true" style={{ color: 'red' }}>*</span>
                    </div>
                    <span id="frequencyRequired" className="emailDigest-sr-only">required</span>
                    <div
                        className="addDigestFrequencySelectContainer"
                        role="group"
                        aria-labelledby="frequencyLabel frequencyRequired"
                    >
                        {FREQUENCIES.map((f, i) => (
                            <span
                                role="button"
                                aria-pressed={currentFrequency === f}
                                tabIndex={0}
                                onClick={() => setCurrentFrequency(f)}
                                onKeyDown={(e) => { if (e.key === 'Enter') setCurrentFrequency(f); }}
                                className={`addDigestFrequencySelect ${currentFrequency === f ? 'active' : ''}`}
                                key={`addDigestFrequency${i}`}
                            >
                                {FREQUENCY_LABELS[f]}
                            </span>
                        ))}
                    </div>
                </section>



                {
                    (currentFrequency === 'WEEKLY' || currentFrequency === 'BIWEEKLY') && <section className='emailDigestSubscriptionsDayOfWeek'>
                        <h3 className='emailDigestSubscriptionsHeader' id="dayOfWeekLabel">Day of Week</h3>
                        <div className='emailDigestSubscriptionsDayOfWeekButtons' role="group" aria-labelledby="dayOfWeekLabel">
                            {
                                DAYS_OF_WEEK.map((d, i) => (<button key={`activityDigestDayOfWeekButton${i}`} onClick={() => selectDayOfWeek(i)} aria-pressed={i === selectedDayOfWeek} className={`${i === selectedDayOfWeek ? 'selected' : ''}`}>{d.label}</button>))
                            }
                        </div>
                    </section>}

                {
                    currentFrequency === 'MONTHLY' && <section>
                        <h3 className='emailDigestSubscriptionsHeader' id="dayOfMonthLabel">Day of Month</h3>
                        <Select value={selectedDayOfMonth} onChange={event => setSelectedDayOfMonth(event.target.value)} name='Day of month' inputProps={{ 'aria-labelledby': 'dayOfMonthLabel' }}>
                            {DAYS_OF_MONTH.map((d, i) => (<MenuItem key={`emailDigestSubscriptionsDayOfMonthOption${i}`} value={d.value}>{d.label}</MenuItem>))}
                        </Select>
                    </section>
                }



                <section className='emailDigestSubscriptionsTime'>
                    <div id="hourOfDayLabel" className="emailDigestSubscriptionsHeader" style={{ display: 'block' }}>
                        Hour of Day
                    </div>
                    <div className='emailDigestSubscriptionsTimeInputsContainer'>
                        <TextField
                            type={'number'}
                            value={getTimeValue(hour)}
                            onChange={updateHour}
                            onKeyPress={validateInput}
                            sx={{ width: '40px', '& input': { padding: '15px 2px' }}}
                            inputProps={{ 'aria-labelledby': 'hourOfDayLabel' }}
                        />
                    </div>
                </section>

                <section className='emailDigestSubscribers'>
                    <div className='emailDigestSubscriptionsHeader' id="subscribersLabel">
                        Subscribers <span aria-hidden="true" style={{ color: 'red' }}>*</span>
                    </div>
                    <EmailChips emailsList={subscribers} addEmails={addEmails} removeItem={removeEmail} onEmailInput={() => setDisableButton(true)} onEmailEmpty={() => setDisableButton(false)} inputAriaLabel="Subscriber Email input" inputAriaRequired="true" inputPlaceholder="Type or paste comma separated email addresses and press `Enter`..." />
                </section>
                <section className='emailDigestSubscriptionFeedback'>
                    <h3 className='emailDigestSubscriptionsHeader' id="feedbackTypesLabel">
                        Types of Feedback <span aria-hidden="true" style={{ color: 'red' }}>*</span>
                    </h3>
                    <span id="feedbackTypesRequired" className="emailDigest-sr-only">required</span>
                    <FormGroup className='emailDigestSubscriptionFeedbackOptions' row role="group" aria-labelledby="feedbackTypesLabel feedbackTypesRequired">
                        {Object.keys(FEEDBACKS).map((key, i) => (
                            <FormControlLabel
                                className='emailDigestSubscriptionFeedbackOption'
                                key={`emailDigestSubscriptionFeedbackOption${i}`}
                                label={FEEDBACKS[key]}
                                control={
                                    <Checkbox
                                        checked={!!selectedFeedbacks[key]}
                                        onChange={() => updateFeedback(key)}
                                        inputProps={{ 'aria-label': FEEDBACKS[key] }}
                                    />
                                }
                            />
                        ))}
                    </FormGroup>
                    {isExpandedSentiment && (
                        <FormGroup style={{ marginTop: '8px' }}>
                            <FormControlLabel
                                label={
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        Include only Noteworthy Feedback
                                        <Tooltip
                                            title="When the Noteworthy Feedback checkbox is selected, the chosen sentiment(s) will trigger the actions only if the Feedback is also marked as Noteworthy. If the Noteworthy Feedback checkbox is not selected, all Feedback matching the chosen sentiment(s) will trigger the actions."
                                            arrow
                                        >
                                            <span
                                                tabIndex={0}
                                                role="button"
                                                aria-roledescription="tooltip"
                                                aria-label="When the Noteworthy Feedback checkbox is selected, the chosen sentiment(s) will trigger the actions only if the Feedback is also marked as Noteworthy. If the Noteworthy Feedback checkbox is not selected, all Feedback matching the chosen sentiment(s) will trigger the actions."
                                                style={{ display: 'inline-flex', cursor: 'pointer' }}
                                            >
                                                <InfoOutlinedIcon
                                                    fontSize="small"
                                                    aria-hidden="true"
                                                    sx={{ verticalAlign: 'middle', color: 'text.secondary' }}
                                                />
                                            </span>
                                        </Tooltip>
                                    </span>
                                }
                                control={
                                    <Checkbox
                                        checked={noteworthy}
                                        onChange={(e) => setNoteworthy(e.target.checked)}
                                        inputProps={{ 'aria-label': 'Include only Noteworthy Feedback' }}
                                    />
                                }
                            />
                        </FormGroup>
                    )}
                </section>

                <div className="actionBar " style={{background:'var(--colorBackgroundPrimary)'}}>
                <div className="actions">
                    <div ref={saveButtonRef} tabIndex={0} onClick={saveSubscription} onKeyDown={(e) => { if (e.key === 'Enter') saveSubscription(); }} className={`button confirm medium`}>
                        <div className="title">Save</div>
                    </div>

                    <div ref={cancelButtonRef} tabIndex={0} onClick={onCancel} onKeyDown={(e) => { if (e.key === 'Enter') onCancel(); else if (e.key === 'Tab' && !e.shiftKey && props.onCancelTab) { e.preventDefault(); props.onCancelTab(); } }} className="button dismiss medium">
                        <div className="title">Cancel</div>
                    </div>
                </div>
                </div>




{/*
                {saving ? <CircularProgress /> : <Button variant="contained" color="primary" onClick={saveSubscription} disabled={disableButton}>Save</Button>}
*/}

                {currentSetting?.logs?.length > 0 ? <section className='emailDigestSubscriptionsSends'>

                    <div className='emailDigestSubscriptionsSendsHeaders'>
                        <span className='emailDigestSubscriptionsSendsHeader'>Time</span>
                        <span className='emailDigestSubscriptionsSendsHeader'>#Sent</span>
                        <span className='emailDigestSubscriptionsSendsHeader'>Errors</span>
                    </div>
                    {currentSetting.logs.map((s, i) => {
                        return (<div className="emailDigestLog" key={`emailDigestLog${i}`}>
                            <span className='emailDigestLogItem'>{convertToTimeZone(s.sentAt, TIMEZONE)}</span>
                            <span className='emailDigestLogItem'>{s.receiversCount}</span>
                            <span className='emailDigestLogItem'>{s.errors || ''}</span>
                        </div>)
                    })}
                </section> : <span>This digest has no sendings history</span>}</> : <CircularProgress />}
        </div>);
}

export default ActivityDigestEmail;